import { useLanguage } from '../context/LanguageContext';
import Avatar from './Avatar';
import { Folder } from 'lucide-react';

/**
 * Área de trabajo vacía con diseño minimalista "Dark Tech Elegante"
 * Muestra la imagen de DamyxIA idéntica al área de conversación
 * Solo se muestra cuando hay un diálogo activo
 */
const EmptyWorkspaceMatrix = ({ showAvatar = false }) => {
  const { t, language } = useLanguage();

  // Helper: dispara el evento que activa forceInternalWorkspace + cambia
  // mobileView a 'workspace'. El listener esta registrado en App.js.
  const openInternalWorkspace = () => {
    try {
      window.dispatchEvent(new Event('damyxia-open-internal-module'));
    } catch (_) { /* noop */ }
  };

  // Boton reusable de "Abrir Workspace Interno" — visible en mobile principalmente,
  // pero util tambien en desktop como atajo si el usuario aterrizo en empty state.
  const openWorkspaceButton = (
    <button
      type="button"
      onClick={openInternalWorkspace}
      data-testid="open-internal-workspace-btn"
      className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/25 transition-colors text-sm font-medium"
    >
      <Folder className="w-4 h-4" />
      {language === 'es' ? 'Abrir Workspace Interno' : 'Open Internal Workspace'}
    </button>
  );
  
  // Si no hay diálogo activo, mostrar solo el texto sin la imagen
  if (!showAvatar) {
    return (
      <div className="relative flex flex-col items-center justify-center h-full overflow-hidden">
        {/* Fondo con patrón sutil de puntos */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.1) 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}
        />
        
        {/* Solo texto "Por DamyxIA" */}
        <div className="relative z-10 text-center">
          <h2 
            className="text-2xl md:text-3xl font-medium tracking-wide"
            style={{
              color: 'rgba(148, 163, 184, 0.5)',
              textShadow: '0 0 20px rgba(148, 163, 184, 0.1)'
            }}
          >
            {t('dashboard.poweredBy')}
          </h2>
          {openWorkspaceButton}
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative flex flex-col items-center justify-center h-full overflow-hidden">
      {/* Fondo con patrón sutil de puntos */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(148, 163, 184, 0.1) 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}
      />
      
      {/* Contenido central con Avatar de DamyxIA - IDÉNTICO al del chat */}
      <div className="relative z-10 text-center">
        {/* Avatar DamyxIA - usando el mismo componente del chat */}
        <div className="mb-6 flex justify-center">
          <Avatar size="xl" />
        </div>
        
        {/* Texto "DamyxIA is Your Choice" */}
        <h2 
          className="text-2xl md:text-3xl font-medium tracking-wide bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent"
          style={{
            textShadow: '0 0 30px rgba(99, 102, 241, 0.3)'
          }}
        >
          {t('dashboard.damyxiaChoice')}
        </h2>
        
        {/* Subtexto */}
        <p className="mt-2 text-sm text-zinc-500">
          {t('dashboard.dragImageHint')}
        </p>

        {openWorkspaceButton}

        {/* Línea decorativa sutil */}
        <div 
          className="mt-4 h-px w-32 mx-auto"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.4), rgba(99, 102, 241, 0.4), transparent)'
          }}
        />
      </div>
      
      {/* Overlay gradiente muy sutil */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, transparent 40%, rgba(15, 23, 42, 0.5) 100%)'
        }}
      />
    </div>
  );
};

export default EmptyWorkspaceMatrix;
